// @ts-strict-ignore
/**
 * Created by vwiny on 10/10/2017.
 */
import { Injectable, ViewContainerRef, ViewChild } from '@angular/core';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMReportPersonalizationDialogComponent } from './lm-report-personalization-dialog.component';
import { Observable, Subject } from 'rxjs';
import { AppConstants, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { LMAddSortDialogComponent, LMAddTotalDialogComponent, LMListSpecUtilService } from '@app/components';
import { LMContextService, LMErrorUtilService, LMModalDialogWrapperRef, LMModalWrapperService } from '@app/services';
import { LMLplEditorComponent } from '@app/components/lplEditor';

export enum LMReportPersonalizationDialogResultCode {
  REPORT_PERSONALIZATION_DIALOG_CANCEL = 0,
  REPORT_PERSONALIZATION_DIALOG_OK,
  REPORT_PERSONALIZATION_DIALOG_RESET,
  REPORT_PERSONALIZATION_DIALOG_SAVED
}


/**
 * Must be a class and not an interface for typescript type guards to work.
 */
export class LMReportPersonalizationDialogResult {
  status: LMReportPersonalizationDialogResultCode;
  displayFields?: any[];
  reportDataView?: LMReportPersonalizationDataView;
  message?: string;
  viewReport?: boolean;
  reportUrl?: string;
}

@Injectable()
export class LMReportPersonalizationDialogService {

  @ViewChild(LMReportPersonalizationDialogComponent) lmReportPersonalizationDialogComponent: LMReportPersonalizationDialogComponent;

  constructor(
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmLoggerService: LMLoggerService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmContextService: LMContextService,
    private lmModalWrapperService: LMModalWrapperService,
    private readonly lmErrorUtilService: LMErrorUtilService,
    private readonly lmListSpecUtilService: LMListSpecUtilService
  ) {}

  private dialog: LMModalDialogWrapperRef<any> = null;
  public runReportPersonalizationDialog(
    mode: LMReportPersonalizationMode,
    placeholder: ViewContainerRef,
    reportType?: LMReportType,
    businessClass?: string,
    listName?: string,
    userFolderId?: string,
    selectedFields?: LMPersonalizationColumn[],
    createReportUrl?: string,
    actorGroupSelectUrl?: string,
    roleSelectUrl?: string,
    roleGroupSelectUrl?: string,
    fieldSearchUrl?: string,
    listTitle?: string,
    listUrlInfo?: LMListUrlInfo,
    listSpec?: LMListSpec,
    dataViewSet?: LMDataViewSet,
    listState?: LMListState,
    personalizationExists?: boolean,
    includeBaseFilter?: boolean,
    filterCondition?: string,
    chartName?: string,
    isReadOnly?: boolean,
    createReportMode?: boolean
  ): Observable<LMReportPersonalizationDialogResult> {
    return new Observable<LMReportPersonalizationDialogResult>(observer => {
      this.lmLoggerService.log([ 'LMReportPersonalizationDialog', 'formService.getFormSpec()', 'Complete' ]);

      const reportPersonalizationDialog$: Subject<LMReportPersonalizationDialogResult> = new Subject<LMReportPersonalizationDialogResult>();
      let dlgComponent: LMReportPersonalizationDialogComponent;
      const dialogId = 'ReportPersonalization';
      const CreateOrCopy = (createReportMode && listSpec?.busListSpec?.isReport && this.lmContextService.simplifyOptionsMenu()) ? LMDialogButtonKey.COPY : LMDialogButtonKey.CREATEREPORT;
      const okButtonKey = ((mode === 'createReportFromMenu' || mode === 'createReportFromBanner') && !personalizationExists) ? CreateOrCopy : LMDialogButtonKey.OK;

      this.lmBusyIndicatorService.hideBusyIndicator();

      const isNotAllowedToContinue = () => {
        const hasDisplayFields = () => dlgComponent.displayFields?.length > 0;
        const hasCardView = () => this.lmListSpecUtilService.hasCardView(dlgComponent.listSpec);
        const isGalleryView = () => this.lmListSpecUtilService.isGallery(dlgComponent.listSpec);

        if (hasCardView() || isGalleryView())
          return false;

        return !hasDisplayFields();
      };

      const buttons: SohoModalButton[] = [
        // TODO: The button default disables the button. A bug in controls?
        // tslint:disable-next-line - shadowed variable e
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: isReadOnly ? Soho.Locale.translate(LMDialogButtonKey.CLOSE) : Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            reportPersonalizationDialog$.complete();
            this.dialog.close(<LMReportPersonalizationDialogResult>{
              status: LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL
            });
          }
        }
      ];

      if (!personalizationExists && mode === 'createReportFromMenu' || mode === 'createReportFromBanner') {
        buttons.push(
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CREATEANDVIEWREPORT ]),
            text: (createReportMode && listSpec?.busListSpec?.isReport && this.lmContextService.simplifyOptionsMenu()) 
              ? Soho.Locale.translate(LMDialogButtonKey.COPYANDVIEWREPORT) 
              : Soho.Locale.translate(LMDialogButtonKey.CREATEANDVIEWREPORT),
            click: () => {
              if (isNotAllowedToContinue()) {
                this.showEmptyDisplayFieldsMessage();
                return;
              }
              reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
                status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_OK,
                reportDataView: dlgComponent.dataView,
                viewReport:     true
              });
            }
          }
        );
      }

      if (personalizationExists && !this.lmContextService.simplifyOptionsMenu() && !isReadOnly) {
        buttons.push(
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.RESET ]),
            text: Soho.Locale.translate(LMDialogButtonKey.RESET),
            click: () => {
              reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
                status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_RESET,
                reportDataView: dlgComponent.dataView
              });
            }
          }
        );
      }

      if(!isReadOnly) {
        buttons.push(
            {
              id: this.lmUniqueIdService.uniqueId([ dialogId, okButtonKey ]),
              text: Soho.Locale.translate(okButtonKey),
              isDefault: true,
              click: () => {
                if (isNotAllowedToContinue()) {
                  this.showEmptyDisplayFieldsMessage();
                  return;
                }
                reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
                  status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_OK,
                  reportDataView: dlgComponent.dataView
                });
              }
            }
        );
      }

      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-personalization-modal' } ];
      const options: SohoModalOptions = {
        fullsize: 'always',
        attributes
      };

      this.dialog = this.lmModalWrapperService
        .modal(LMReportPersonalizationDialogComponent, options)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((ReportPersonalization: LMReportPersonalizationDialogComponent) => {
          dlgComponent = ReportPersonalization;
          dlgComponent.mode = mode;
          dlgComponent.personalizationExists = personalizationExists;
          dlgComponent.createReportMode = createReportMode;
          dlgComponent.readOnly = isReadOnly;

          if (createReportUrl) {
            dlgComponent.url = createReportUrl;
          }
          if (actorGroupSelectUrl) {
            dlgComponent.actorGroupUrl = actorGroupSelectUrl;
          }
          if (roleSelectUrl) {
            dlgComponent.roleUrl = roleSelectUrl;
          }
          if (roleGroupSelectUrl) {
            dlgComponent.roleGroupUrl = roleGroupSelectUrl;
          }
          if (fieldSearchUrl) {
            dlgComponent.fieldSearchUrl = fieldSearchUrl;
          }
          if (businessClass) {
            dlgComponent.businessClass = businessClass;
          }
          if (listName) {
            dlgComponent.listName = listName;
          }
          if (reportType) {
            dlgComponent.reportType = reportType;
          }
          if (userFolderId) {
            dlgComponent.userFolderId = userFolderId;
          }
          if (selectedFields) {
            dlgComponent.selectedFields = selectedFields;
          }
          if (listTitle) {
            dlgComponent.listTitle = listTitle;
          }
          if (listSpec) {
            // dlgComponent.listTitle = listTitle;
            dlgComponent.listUrlInfo = listUrlInfo;
            dlgComponent.listSpec = listSpec;
            dlgComponent.dataViewSet = dataViewSet;
            dlgComponent.cubeViewState = listState;
            dlgComponent.includeBaseFilter = includeBaseFilter;
            dlgComponent.filterCondition = filterCondition;

            if(chartName) {
              dlgComponent.chartName = chartName;
            }
          }

          dlgComponent.dialog$ = reportPersonalizationDialog$;
          this.lmReportPersonalizationDialogComponent = dlgComponent;
        })
        .afterClose(result => {
          this.dialog = null;
          this.lmReportPersonalizationDialogComponent = null;
          observer.next(result);
          observer.complete();
        });

      reportPersonalizationDialog$.subscribe((result: LMReportPersonalizationDialogResult) => {
        if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED) {
          reportPersonalizationDialog$.complete();
          this.dialog.close(result);
        }
      });
    });
  }

 public openLPLEditorDialog(title: string, lpl: string, validationOptions?: LMEditorValidationOptions) {
  
  let lplEditorComponent: LMLplEditorComponent;
  let updatedLPL = lpl;

  const editorName = 'LMListPersonalizationEditLPLDialog';
  const returnQuery = new Subject<LMListPersonalizationEditLPLDialogResponse>();

  const response = (result: LMListPersonalizationEditLPLDialogResponse) => {
    returnQuery.next(result);
  };

  const closeLPLEditorDialog = () => {
    modalRef.close(true);
    returnQuery.complete();
  };

  const buttons = [];

  buttons.push(
      {
        id: this.lmUniqueIdService.uniqueId([ editorName, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => {
          // check unsaved
          response({
            status: LMDialogButtonKey.CANCEL,
            hasChanges: lpl !== updatedLPL,
            closeLPLEditorDialog
          });

        }
      }
  );

  buttons.push(
    {
      id: this.lmUniqueIdService.uniqueId([ editorName, LMDialogButtonKey.SAVE ]),
      text: Soho.Locale.translate(LMDialogButtonKey.SAVE),
      isDefault: true,
      click: () => response({
        status: LMDialogButtonKey.SAVE,
        lpl: updatedLPL,
        hasChanges: lpl !== updatedLPL,
        closeLPLEditorDialog
      })
    }
  );

  const attributes: SohoAttributesArray = [ {
    name: 'data-automation-id',
    value: `lm-list-personalization-edit-lpl-dialog`
  } ];

  const sohoModalOptions: SohoModalOptions = {
    fullsize: 'responsive',
    attributes
  };

  const modalRef = this.lmModalWrapperService
    .modal<LMLplEditorComponent>(LMLplEditorComponent, sohoModalOptions)
    .title(title)
    .buttons(buttons)
    .isAlert(false)
    .apply((dialogComponent: LMLplEditorComponent) => {
      lplEditorComponent = dialogComponent;
      lplEditorComponent.lpl = lpl;
      lplEditorComponent.validationOptions = validationOptions;
      lplEditorComponent.lplChangedEvent.subscribe(result => {
        updatedLPL = result;
      });
    })
    .open();

    return returnQuery.asObservable();
 }

  public openAddSortDialog(request: LMAddSortRequest) {
    const title = request.columns ? Soho.Locale.translate('AddSortToColumn') :  Soho.Locale.translate('AddSort');

    return new Observable<LMAddSortResponse>(observer => {
      const dialogId = 'AddSort';

      const buttons: SohoModalButton[] = [
        {
          id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            addSortDialog.close(<LMAddSortResponse>{
              column: undefined,
              sortOrder: undefined,
              status: 'CANCEL'
            });
          }
        },
        {
          id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.OK),
          isDefault: true,
          click: (e, modal) => {
            if (dialogComponent.onDialogSubmit()) {
              const response = dialogComponent.getResponse();
              response.status = 'OK';
              addSortDialog.close(response);
            }
          }
        }
      ];


      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-add-sort-modal' } ];
      const options: SohoModalOptions = {
        attributes
      };

      let dialogComponent: LMAddSortDialogComponent;
      const addSortDialog = this.lmModalWrapperService
        .modal(LMAddSortDialogComponent, options)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((Component: LMAddSortDialogComponent) => {
          dialogComponent = Component;
          dialogComponent.request = request;
        })
        .afterClose(result => {
          observer.next(result);
          observer.complete();
        });
    });
  }

  public openAddTotalDialog(request: LMAddTotalRequest) {
    const title = Soho.Locale.translate('AddSummaryTotal');

    return new Observable<LMAddTotalResponse>(observer => {
      const dialogId = 'AddTotalField';

      const buttons: SohoModalButton[] = [
        {
          id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            addSortDialog.close(<LMAddSortResponse>{
              column: undefined,
              sortOrder: undefined,
              status: 'CANCEL'
            });
          }
        },
        {
          id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.OK),
          isDefault: true,
          click: (e, modal) => {
            dialogComponent.onDialogSubmit().subscribe(close => {
              if (close) {
                const response = dialogComponent.getResponse();
                response.status = 'OK';
                addSortDialog.close(response);
              }
            });
          }
        }
      ];


      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-report-add-total-modal' } ];
      const options: SohoModalOptions = {
        attributes
      };

      let dialogComponent: LMAddTotalDialogComponent;
      const addSortDialog = this.lmModalWrapperService
        .modal(LMAddTotalDialogComponent, options)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((Component: LMAddTotalDialogComponent) => {
          dialogComponent = Component;
          dialogComponent.request = request;
        })
        .afterClose(result => {
          observer.next(result);
          observer.complete();
        });
    });
  }

  public isDataSourceChangeable(model: LMListPersonalizationInfo): boolean {
    if (!model.isReport) {
      return false;
    }
    return !(model.dataOption === 'analytics' || model.dataOption === 'columnar');
  }
  public isAsOfDateCapable(model: LMListPersonalizationInfo): boolean {
    if (!model.isReport) {
      return false;
    }

    if (!this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_AsOfDate)) {
      return false;
    }

    // business class is capable of time travel
    const isCapable = model.implementsAuditing || model.implementsEffectiveDated;

    if (isCapable) {
      // is the datasource capable of time travel
      return model.dataOption === 'live' || model.dataOption === 'archive';
    } else {
      return false;
    }
  }

  public buildDataSourceText(model: LMListPersonalizationInfo): string{
    switch (model.dataOption) {
      case 'audit':
        return  Soho.Locale.translate('ListPersonalizationSourceTransactionAudit');
      case 'archive':
        return Soho.Locale.translate('ListPersonalizationSourceArchive');
      case 'auditArchive':
        return Soho.Locale.translate('ListPersonalizationSourceArchiveAudit');
      case 'columnar':
        return Soho.Locale.translate('ListPersonalizationSourceColumnar');
      case 'analytics':
        return Soho.Locale.translate('ListPersonalizationSourceAnalytics');
      case 'live':
      default:
        return Soho.Locale.translate('ListPersonalizationSourceTransaction');
    }
  }

  public buildAsOfDateText(model: LMListPersonalizationInfo): string{
    switch (model.dataOption) {
      case 'audit':
      case 'auditArchive':
      case 'columnar':
      case 'analytics':
        return  '';
      case 'archive':
      case 'live':
      default: {
        return this.formatAsOfDate(model);
      }
    }
  }

  public formatAsOfDate(model: LMListPersonalizationInfo): string {
    if (model.asOfDate) {
      return model.asOfDate;
    } else {
      return Soho.Locale.translate('Now');
    }
  }

  private showEmptyDisplayFieldsMessage() {
    this.lmErrorUtilService.handleViewException({
      message: Soho.Locale.translate('NoDisplayFieldError'),
    });
  }
}
